import type { PackEntry } from 'types/shared';
import type { CreativeDnDMovePayload } from '../../types/hypothesis';
import {
  getHypothesisCreativesIdToHypothesis,
  isMixedPackEntry,
  packHypothesisKeyFromPack,
} from '../../utils/hypothesisPackUtils';

const removeCreativeFromPack = (pack: PackEntry, creativeId: string, index: number) => {
  if (index >= 0 && pack.creatives[index] === creativeId) {
    pack.creatives.splice(index, 1);
    return;
  }
  const idx = pack.creatives.indexOf(creativeId);
  if (idx >= 0) pack.creatives.splice(idx, 1);
};

const packMatchesCreativeHypothesis = (
  pack: PackEntry,
  creativeHypothesis: string | undefined,
): boolean =>
  !isMixedPackEntry(pack) &&
  packHypothesisKeyFromPack(pack) === creativeHypothesis;

export const applyPackCreativesMove = (
  packs: PackEntry[],
  { creativeId, from, to }: CreativeDnDMovePayload,
  idToHypothesis: ReadonlyMap<string, string> = getHypothesisCreativesIdToHypothesis(),
): PackEntry[] => {
  const next = packs.map((p) => ({ ...p, creatives: [...p.creatives] }));

  if (from.kind === 'unpacked' && to.kind === 'unpacked') {
    return next;
  }

  if (from.kind === 'pack' && to.kind === 'pack' && from.packIndex === to.packIndex) {
    const p = next[from.packIndex];
    if (!p) return next;
    const arr = [...p.creatives];
    const fromIdx =
      arr[from.index] === creativeId ? from.index : arr.indexOf(creativeId);
    if (fromIdx < 0) return next;
    const [removed] = arr.splice(fromIdx, 1);
    const insertAt = Math.min(Math.max(0, to.index), arr.length);
    arr.splice(insertAt, 0, removed);
    p.creatives = arr;
    return next;
  }

  if (from.kind === 'pack' && to.kind === 'unpacked') {
    const sourcePack = next[from.packIndex];
    if (sourcePack) {
      removeCreativeFromPack(sourcePack, creativeId, from.index);
    }
    return next;
  }

  if (to.kind === 'pack') {
    const targetPack = next[to.packIndex];
    if (!targetPack) return next;

    const targetIsMixed = isMixedPackEntry(targetPack);
    const creativeKey = idToHypothesis.get(creativeId);
    const isSameHypothesisTarget = packMatchesCreativeHypothesis(
      targetPack,
      creativeKey,
    );

    if (!targetPack.creatives.includes(creativeId)) {
      const insertAt = Math.min(Math.max(0, to.index), targetPack.creatives.length);
      targetPack.creatives.splice(insertAt, 0, creativeId);
    }

    if (!targetIsMixed && isSameHypothesisTarget) {
      for (let i = 0; i < next.length; i++) {
        if (i === to.packIndex) continue;
        const pack = next[i];
        if (!pack || !packMatchesCreativeHypothesis(pack, creativeKey)) continue;
        removeCreativeFromPack(pack, creativeId, 0);
      }
    }
  }

  return next;
};

export interface PruneEmptyPacksResult {
  packs: PackEntry[];
  removedIndices: number[];
}

/** Drops packs with no creatives (e.g. after dragging the last creative out). */
export const pruneEmptyPacks = (packs: PackEntry[]): PruneEmptyPacksResult => {
  const removedIndices: number[] = [];
  const nextPacks = packs.filter((pack, index) => {
    if (pack.creatives.length > 0) return true;
    removedIndices.push(index);
    return false;
  });
  return { packs: nextPacks, removedIndices };
};

export const adjustActivePackIndexAfterRemoval = (
  activeIndex: number | null,
  removedIndices: number[],
): number | null => {
  if (activeIndex === null || removedIndices.length === 0) return activeIndex;
  if (removedIndices.includes(activeIndex)) return null;
  const shift = removedIndices.filter((index) => index < activeIndex).length;
  return activeIndex - shift;
};
